import { FFmpeg, downloadUint8Array, guessMimeType } from '@wasmedia/wffmpeg';

type Element<T extends HTMLElement> = T;

const $ = <T extends HTMLElement>(id: string): Element<T> => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el as T;
};

const status = $<HTMLDivElement>('status');
const runBtn = $<HTMLButtonElement>('runBtn');
const downloadBtn = $<HTMLButtonElement>('downloadBtn');
const inputFile = $<HTMLInputElement>('inputFile');
const commandInput = $<HTMLInputElement>('command');
const progressDiv = $<HTMLDivElement>('progress');
const progressBar = $<HTMLProgressElement>('progressBar');
const progressText = $<HTMLSpanElement>('progressText');
const outputVideo = $<HTMLVideoElement>('outputVideo');
const isolationSpan = $<HTMLSpanElement>('isolation');

if (window.crossOriginIsolated) {
  isolationSpan.textContent = 'Cross-origin isolated — pthreaded core ready.';
  isolationSpan.dataset.state = 'ok';
} else {
  isolationSpan.textContent =
    'Not yet cross-origin isolated. The COI service worker will install on first load — the page will reload automatically.';
  isolationSpan.dataset.state = 'warn';
}

let outputData: Uint8Array | null = null;

const log = (msg: string) => {
  status.textContent += msg + '\n';
  status.scrollTop = status.scrollHeight;
};

const ffmpeg = new FFmpeg();

log('Loading FFmpeg...');
await ffmpeg.load({
  log: false,
  // Static paths — the build script emits worker.js next to index.html
  // and copies the core into ./wasm/.
  workerURL: new URL('worker.js', window.location.href).href,
  coreURL: new URL('wasm/ffmpeg-core.js', window.location.href).href,
  wasmURL: new URL('wasm/ffmpeg-core.wasm', window.location.href).href,
});
log('FFmpeg loaded.');
runBtn.disabled = false;

ffmpeg.on('log', ({ type, message }: { type: string; message: string }) => {
  if (type === 'fferr') log(message);
});

ffmpeg.on('progress', ({ progress }: { progress: number }) => {
  const pct = (progress * 100).toFixed(1);
  progressBar.value = Number(pct);
  progressText.textContent = pct + '%';
});

const outputNameFrom = (cmd: string) => {
  const args = cmd.trim().split(/\s+/);
  return args[args.length - 1];
};

runBtn.addEventListener('click', async () => {
  const file = inputFile.files?.[0];
  if (!file) {
    log('Error: select an input file first.');
    return;
  }

  runBtn.disabled = true;
  downloadBtn.disabled = true;
  outputVideo.style.display = 'none';
  progressDiv.style.display = 'block';
  status.textContent = '';

  try {
    log(`Writing ${file.name} (${file.size} bytes)...`);
    const inputData = new Uint8Array(await file.arrayBuffer());
    await ffmpeg.writeFile('input', inputData);

    const command = commandInput.value.trim();
    log(`Running: ffmpeg ${command}`);

    const exitCode = await ffmpeg.run(command);
    log(`Exit code: ${exitCode}`);

    if (exitCode === 0) {
      const outputName = outputNameFrom(command);
      outputData = await ffmpeg.readFile(outputName);
      log(`Output: ${outputName} (${outputData.byteLength} bytes)`);

      const mime = guessMimeType(outputName);
      if (mime.startsWith('video/') || mime.startsWith('audio/')) {
        const blob = new Blob([outputData], { type: mime });
        outputVideo.src = URL.createObjectURL(blob);
        outputVideo.style.display = 'block';
      }

      downloadBtn.disabled = false;
    }
  } catch (err) {
    log(`Error: ${(err as Error)?.message ?? String(err)}`);
  }

  runBtn.disabled = false;
  progressDiv.style.display = 'none';
});

downloadBtn.addEventListener('click', () => {
  if (!outputData) return;
  const outputName = outputNameFrom(commandInput.value);
  downloadUint8Array(outputData, outputName, guessMimeType(outputName));
});
